import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import saasAdminApi from '../services/saasAdminApi';
import adminApi from '../services/adminApi';
import { formatApiError } from '../utils/errorMessage';
import ErrorBoundary from '../components/ErrorBoundary';
import PlatformPlans from '../components/PlatformPlans';
import BillingExemptions, { ExemptBadge } from '../features/billing-exempt/BillingExemptions';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  UsersIcon, BuildingOfficeIcon, CreditCardIcon,
  ChartBarIcon, CurrencyDollarIcon, ClockIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon,
  ArrowPathIcon, ServerStackIcon, GlobeAltIcon,
  EyeIcon, ArrowRightIcon, UserGroupIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { userBehaviorTrackingAPI } from '../services/userBehaviorTrackingApi';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [behaviorAnalytics, setBehaviorAnalytics] = useState(null);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState({
    backend: 'idle',
    frontend: 'idle',
    ui: 'idle'
  });
  const [serviceMessage, setServiceMessage] = useState('');
  const [period, setPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSortField, setUserSortField] = useState(null);
  const [userSortDirection, setUserSortDirection] = useState('asc');

  const canView = user && (user.is_superuser || user.role === 'superuser');

  useEffect(() => {
    if (canView) {
      loadData();
      loadBehaviorAnalytics();
    }
  }, [canView, period]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, subscriptionsRes, paymentsRes, activityRes] = await Promise.all([
        saasAdminApi.getStats(period),
        saasAdminApi.listSubscriptions({ limit: 50 }),
        saasAdminApi.listPayments({ limit: 20 }),
        saasAdminApi.getActivity({ limit: 50 })
      ]);

      setStats(statsRes.data);
      setSubscriptions(subscriptionsRes.data || []);
      setPayments(paymentsRes.data || []);
      setActivity(activityRes.data || []);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load SaaS statistics'));
    } finally {
      setLoading(false);
    }
  };

  const loadBehaviorAnalytics = async () => {
    try {
      setBehaviorLoading(true);
      const response = await userBehaviorTrackingAPI.getAnalytics({});
      setBehaviorAnalytics(response.data);
    } catch (e) {
      // Silently fail - don't show error for analytics
      // Set empty data if migration not run
      if (e.response?.status === 500 || e.response?.status === 503) {
        setBehaviorAnalytics({
          page_visits: [],
          navigation_flow: [],
          session_stats: [],
          total_sessions: 0
        });
      }
      console.debug('Failed to load behavior analytics:', e?.response?.status || e?.code || 'no response', e?.response?.data?.code || '');
    } finally {
      setBehaviorLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleServiceRestart = async (target) => {
    setServiceStatus((prev) => ({ ...prev, [target]: 'working' }));
    setServiceMessage('');

    try {
      if (target === 'ui') {
        try {
          localStorage.clear();
        } catch {}
        try {
          sessionStorage.clear();
        } catch {}
        if (window.caches) {
          window.caches.keys().then((keys) => keys.forEach((key) => window.caches.delete(key))).catch(() => {});
        }
        setServiceStatus((prev) => ({ ...prev, ui: 'success' }));
        setServiceMessage('Cleared UI cache/state. Reloading...');
        setTimeout(() => window.location.reload(), 800);
        return;
      }

      const response = await adminApi.restartServer(target);
      const handled = response?.data?.handled !== false;
      const message = response?.data?.message || (handled
        ? 'Restart command sent successfully.'
        : 'Restart command could not be handled automatically.');

      setServiceStatus((prev) => ({ ...prev, [target]: handled ? 'success' : 'error' }));
      setServiceMessage(message);

      if (target === 'frontend') {
        setTimeout(() => {
          window.location.reload();
        }, handled ? 2500 : 1200);
      }
    } catch (e) {
      const friendly = formatApiError(e, 'Failed to restart service');
      setServiceStatus((prev) => ({ ...prev, [target]: 'error' }));
      setServiceMessage(friendly);
    }
  };

  if (!canView) {
    return (
      <ErrorBoundary section="Admin Dashboard">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">You do not have permission to view this page. Superuser access required.</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (loading && !stats) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading SaaS statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overview = stats?.overview || {};
  const growth = stats?.growth || {};
  const activityData = stats?.activity || {};
  const subscriptionData = stats?.subscriptions || {};

  return (
    <ErrorBoundary section="Admin Dashboard">
      <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SaaS Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Comprehensive platform statistics and management</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
            className="border rounded-lg px-4 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <ServerControls
        serviceStatus={serviceStatus}
        onRestart={handleServiceRestart}
        serviceMessage={serviceMessage}
      />

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'subscriptions', 'payments', 'activity', 'analytics'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <PlatformPlans />
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Users"
              value={overview.totalUsers || 0}
              subtitle={`${overview.activeUsers || 0} active`}
              icon={UsersIcon}
              trend={growth.users?.length > 0 ? growth.users[growth.users.length - 1]?.count : null}
            />
            <MetricCard
              title="Total Businesses"
              value={overview.totalBusinesses || 0}
              subtitle={`${overview.activeBusinesses || 0} active`}
              icon={BuildingOfficeIcon}
              trend={growth.businesses?.length > 0 ? growth.businesses[growth.businesses.length - 1]?.count : null}
            />
            <MetricCard
              title="Monthly Recurring Revenue"
              value={formatCurrency(overview.monthlyRecurringRevenue || 0)}
              subtitle={`${overview.activeSubscriptions || 0} active subscriptions`}
              icon={CurrencyDollarIcon}
              trend={null}
            />
            <MetricCard
              title="Total Revenue"
              value={formatCurrency(overview.totalRevenue || 0)}
              subtitle={`${overview.recentPayments?.count || 0} payments (${period}d)`}
              icon={CreditCardIcon}
              trend={overview.recentPayments?.total ? formatCurrency(overview.recentPayments.total) : null}
            />
          </div>

          {/* Growth Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">User Growth</h3>
              {growth.users && growth.users.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={growth.users}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" name="New Users" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No growth data available
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Business Growth</h3>
              {growth.businesses && growth.businesses.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={growth.businesses}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#10b981" name="New Businesses" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No growth data available
                </div>
              )}
            </div>
          </div>

          {/* Activity Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Activity by Type</h3>
              {activityData.byType && activityData.byType.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={activityData.byType}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="action_type" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No activity data available
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Subscription Status</h3>
              {subscriptionData.byStatus && subscriptionData.byStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={subscriptionData.byStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {subscriptionData.byStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No subscription data available
                </div>
              )}
            </div>
          </div>

          {/* User Behavior Analytics Summary */}
          {behaviorAnalytics && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <EyeIcon className="h-6 w-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">User Behavior Analytics</h3>
                    <p className="text-sm text-gray-600">Track user navigation and engagement patterns</p>
                  </div>
                </div>
                <Link
                  to="/admin/analytics"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <span>View Full Analytics</span>
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600">Total Sessions</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">
                    {behaviorAnalytics.total_sessions || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600">Pages Tracked</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">
                    {behaviorAnalytics.page_visits?.length || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600">Total Views</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">
                    {behaviorAnalytics.page_visits?.reduce((sum, p) => sum + (parseInt(p.visit_count) || 0), 0) || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600">Top Page</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1 truncate">
                    {behaviorAnalytics.page_visits?.[0]?.page_name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Top Active Users */}
          {activityData.topUsers && activityData.topUsers.length > 0 && (() => {
            // Filter users based on search query
            const filteredUsers = activityData.topUsers.filter((user) => {
              if (!userSearchQuery.trim()) return true;
              const query = userSearchQuery.toLowerCase();
              const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
              const email = (user.email || '').toLowerCase();
              return email.includes(query) || fullName.includes(query);
            });

            // Sort users
            const sortedUsers = [...filteredUsers].sort((a, b) => {
              if (!userSortField) return 0;
              
              let aValue, bValue;
              
              switch (userSortField) {
                case 'user':
                  // Sort by email
                  aValue = (a.email || '').toLowerCase();
                  bValue = (b.email || '').toLowerCase();
                  break;
                case 'login_count':
                  aValue = a.login_count || 0;
                  bValue = b.login_count || 0;
                  break;
                case 'last_activity':
                  aValue = a.last_activity ? new Date(a.last_activity).getTime() : 0;
                  bValue = b.last_activity ? new Date(b.last_activity).getTime() : 0;
                  break;
                default:
                  return 0;
              }
              
              if (aValue < bValue) return userSortDirection === 'asc' ? -1 : 1;
              if (aValue > bValue) return userSortDirection === 'asc' ? 1 : -1;
              return 0;
            });

            const handleSort = (field) => {
              if (userSortField === field) {
                // Toggle direction
                setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
              } else {
                // New field, default to ascending
                setUserSortField(field);
                setUserSortDirection('asc');
              }
            };

            const SortIcon = ({ field }) => {
              if (userSortField !== field) {
                return (
                  <span className="ml-1 text-gray-400">
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </span>
                );
              }
              return (
                <span className="ml-1 text-blue-600">
                  {userSortDirection === 'asc' ? (
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </span>
              );
            };

            return (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Top Active Users</h3>
                  <div className="w-64">
                    <input
                      type="text"
                      placeholder="Search users by email or name..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('user')}
                        >
                          <div className="flex items-center">
                            User
                            <SortIcon field="user" />
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('login_count')}
                        >
                          <div className="flex items-center">
                            Login Count
                            <SortIcon field="login_count" />
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('last_activity')}
                        >
                          <div className="flex items-center">
                            Last Activity
                            <SortIcon field="last_activity" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedUsers.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                            {userSearchQuery ? 'No users found matching your search' : 'No users found'}
                          </td>
                        </tr>
                      ) : (
                        sortedUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.first_name} {user.last_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.login_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.last_activity)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {userSearchQuery && (
                  <div className="mt-2 text-sm text-gray-500">
                    Showing {sortedUsers.length} of {activityData.topUsers.length} users
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-6">
          <BillingExemptions />
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Business Subscriptions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monthly Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period End
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        No subscriptions found
                      </td>
                    </tr>
                  ) : (
                    subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sub.business_name}</div>
                          <div className="text-sm text-gray-500">{sub.business_owner_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          {sub.plan_name}
                          <ExemptBadge row={sub} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(sub.monthly_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              sub.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : sub.status === 'trial'
                                ? 'bg-blue-100 text-blue-800'
                                : sub.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(sub.current_period_end)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(sub.total_paid || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              // TODO: Implement subscription management modal
                              alert('Subscription management coming soon');
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Payment History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No payments found
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(payment.payment_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{payment.business_name}</div>
                          <div className="text-sm text-gray-500">{payment.plan_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              payment.payment_status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : payment.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : payment.payment_status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {payment.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {payment.payment_method || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.invoice_number || 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Quick Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Sessions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {behaviorAnalytics?.total_sessions || 0}
                  </p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pages Tracked</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {behaviorAnalytics?.page_visits?.length || 0}
                  </p>
                </div>
                <EyeIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Page Views</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {behaviorAnalytics?.page_visits?.reduce((sum, p) => sum + (parseInt(p.visit_count) || 0), 0) || 0}
                  </p>
                </div>
                <ChartBarIcon className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Time/Page</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {behaviorAnalytics?.page_visits?.length > 0
                      ? Math.round(
                          behaviorAnalytics.page_visits.reduce((sum, p) => sum + (parseFloat(p.avg_time_spent) || 0), 0) /
                          behaviorAnalytics.page_visits.length
                        )
                      : 0}s
                  </p>
                </div>
                <ClockIcon className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </div>

          {/* Link to Full Analytics */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">User Behavior Analytics</h3>
                <p className="text-gray-600">
                  View detailed page visit statistics, navigation flow, and session analytics
                </p>
              </div>
              <Link
                to="/admin/analytics"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <span>View Full Analytics</span>
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>

          {/* Top Pages Chart */}
          {behaviorAnalytics?.page_visits && behaviorAnalytics.page_visits.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Top 10 Most Visited Pages</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={behaviorAnalytics.page_visits.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="page_name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="visit_count" fill="#3b82f6" name="Visits" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Average Time Spent (Top 10)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={behaviorAnalytics.page_visits.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="page_name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => `${Math.round(value)}s`} />
                    <Legend />
                    <Bar dataKey="avg_time_spent" fill="#10b981" name="Avg Time (seconds)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Pages Table */}
          {behaviorAnalytics?.page_visits && behaviorAnalytics.page_visits.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Page Visit Summary</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Page
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Visits
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {behaviorAnalytics.page_visits.slice(0, 15).map((page, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {page.page_name || page.page_path}
                          </div>
                          <div className="text-sm text-gray-500">{page.page_path}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {page.visit_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Math.round(parseFloat(page.avg_time_spent) || 0)}s
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Math.round(parseInt(page.total_time_spent) || 0)}s
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Navigation Flow Preview */}
          {behaviorAnalytics?.navigation_flow && behaviorAnalytics.navigation_flow.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Top Navigation Patterns</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From Page
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        To Page
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transitions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {behaviorAnalytics.navigation_flow.slice(0, 20).map((flow, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {flow.from_page}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {flow.to_page}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {flow.transition_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {behaviorLoading && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading behavior analytics...</p>
                </div>
              </div>
            </div>
          )}

          {!behaviorLoading && (!behaviorAnalytics || 
            (behaviorAnalytics.page_visits?.length === 0 && 
             behaviorAnalytics.navigation_flow?.length === 0)) && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center py-8">
                <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No behavior tracking data available yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  User behavior tracking will appear here once users start navigating the application
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Recent User Activity</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resource
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activity.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                        No activity found
                      </td>
                    </tr>
                  ) : (
                    activity.map((log) => (
                      <tr key={log.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{log.user_email}</div>
                          {log.first_name && (
                            <div className="text-sm text-gray-500">
                              {log.first_name} {log.last_name}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.business_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {log.action_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.resource_type || 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      </div>
    </ErrorBoundary>
  );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, trend }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg">
          <Icon className="h-6 w-6 text-blue-600" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center text-sm">
          <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
          <span className="text-gray-600">{trend}</span>
        </div>
      )}
    </div>
  );
};

const ServerControls = ({ serviceStatus, onRestart, serviceMessage }) => {
  const controls = [
    {
      key: 'backend',
      title: 'Backend API',
      description: 'Restart the Node/Express API server.',
      icon: ServerStackIcon,
      actionText: 'Restart API',
      warning: 'Available in development/superuser mode.'
    },
    {
      key: 'frontend',
      title: 'Frontend UI',
      description: 'Restart the React/Vite dev server (if configured) and reload the browser.',
      icon: GlobeAltIcon,
      actionText: 'Restart UI Server',
      warning: 'Requires FRONTEND_RESTART_COMMAND. Falls back to browser refresh.'
    },
    {
      key: 'ui',
      title: 'UX / Cache Reset',
      description: 'Clear local storage/session storage and cached assets.',
      icon: ChartBarIcon,
      actionText: 'Reset UX Cache',
      warning: 'Clears saved preferences before reload.'
    }
  ];

  const getButtonState = (status) => {
    switch (status) {
      case 'working':
        return { text: 'Working...', disabled: true, className: 'bg-blue-200 text-blue-800' };
      case 'success':
        return { text: 'Completed', disabled: false, className: 'bg-green-100 text-green-700' };
      case 'error':
        return { text: 'Retry', disabled: false, className: 'bg-red-100 text-red-700' };
      default:
        return { text: null, disabled: false, className: '' };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ArrowPathIcon className="h-5 w-5 text-blue-500" />
            Service Controls
          </h2>
          <p className="text-sm text-gray-500">
            Restart backend services or refresh the frontend / UX cache to make sure the latest changes are applied.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {controls.map((control) => {
          const state = serviceStatus[control.key];
          const buttonState = getButtonState(state);

          return (
            <div key={control.key} className="border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <control.icon className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">{control.title}</p>
                    <p className="text-sm text-gray-500">{control.description}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{control.warning}</p>
              </div>
              <button
                onClick={() => onRestart(control.key)}
                disabled={buttonState.disabled}
                className={`mt-4 inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 ${
                  buttonState.disabled ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {buttonState.text || control.actionText}
              </button>
            </div>
          );
        })}
      </div>

      {serviceMessage && (
        <div className="mt-4 rounded-md bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800">
          {serviceMessage}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
